/**
 * Prov API Client
 *
 * Completely separate from the visitor kiosk API.
 * Uses its own kiosk token stored in localStorage under a different key.
 */

import axios, { AxiosInstance } from 'axios';

const PROV_TOKEN_KEY = 'kioskProvToken';
const PROV_ADMIN_TOKEN_KEY = 'provAdminToken';
const API_BASE_URL = process.env.REACT_APP_API_URL || '';

const provAxios: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
  withCredentials: true, // Needed for express-session cookies (admin)
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth header to all requests
provAxios.interceptors.request.use((config) => {
  const token = getProvToken();
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  const adminToken = getProvAdminToken();
  if (adminToken) {
    config.headers['x-prov-admin-token'] = adminToken;
  }
  return config;
});

// =============================================================================
// Token management
// =============================================================================

export function getProvToken(): string | null {
  return localStorage.getItem(PROV_TOKEN_KEY);
}

export function setProvToken(token: string): void {
  localStorage.setItem(PROV_TOKEN_KEY, token);
}

export function clearProvToken(): void {
  localStorage.removeItem(PROV_TOKEN_KEY);
  clearProvAdminToken();
}

export function hasProvToken(): boolean {
  const token = getProvToken();
  return token !== null && token.length > 0;
}

export function isProvTokenAuthError(error: unknown): boolean {
  if (!axios.isAxiosError(error)) return false;
  const status = error.response?.status;
  const message = String((error.response?.data as { message?: string } | undefined)?.message || '').toLowerCase();
  if (status === 401) return true;
  if (status === 403 && /token|authorization|prov kiosk|prov-token|kiosk/.test(message)) {
    return true;
  }
  return false;
}

export async function testProvToken(candidate: string): Promise<boolean> {
  try {
    await axios.get<StatsResponse>(`${API_BASE_URL}/api/prov/stats`, {
      timeout: 15000,
      withCredentials: true,
      headers: {
        Authorization: `Bearer ${candidate}`,
      },
    });
    return true;
  } catch {
    return false;
  }
}

export async function activateProvToken(candidate: string): Promise<boolean> {
  const trimmed = candidate.trim();
  const isValid = await testProvToken(trimmed);
  if (!isValid) {
    return false;
  }
  setProvToken(trimmed);
  return true;
}

// =============================================================================
// Types
// =============================================================================

export interface ProvWordForm {
  label: string;
  value?: string; // Only present in admin full view
}

export interface ProvSetSummary {
  _id: string;
  title: string;
  subject?: string;
  source?: string;
  type: 'standard' | 'glosor';
  questionCount: number;
  revealAfterAnswer: boolean;
  attemptCount?: number;
  createdAt: string;
}

export interface ProvQuestion {
  _id: string;
  prompt: string;
  imageUrl?: string;
  order: number;
  /** Form labels only (no values) — used for böjning display in student view */
  forms?: Array<{ label: string }>;
}

export interface ProvQuestionFull {
  _id: string;
  prompt: string;
  answer: string;
  acceptableAnswers?: string[];
  imageUrl?: string;
  order: number;
  forms?: ProvWordForm[];
}

export interface ProvSetStudent {
  _id: string;
  title: string;
  subject?: string;
  source?: string;
  type: 'standard' | 'glosor';
  questions: ProvQuestion[];
  revealAfterAnswer: boolean;
  createdAt: string;
}

export interface ProvSetFull {
  _id: string;
  title: string;
  subject?: string;
  source?: string;
  type: 'standard' | 'glosor';
  questions: ProvQuestionFull[];
  archived: boolean;
  revealAfterAnswer: boolean;
  /** Number of completed attempts (populated by admin list endpoint) */
  attemptCount?: number;
  createdAt: string;
  updatedAt: string;
}

export interface ProvSetExportPayload {
  version: number;
  exportedAt: string;
  set: {
    title: string;
    subject?: string;
    source?: string;
    type: 'standard' | 'glosor';
    revealAfterAnswer: boolean;
    questions: Array<{
      prompt: string;
      answer: string;
      acceptableAnswers?: string[];
      imageUrl?: string;
      forms?: ProvWordForm[];
    }>;
  };
}

export interface ProvAttemptSummary {
  _id: string;
  setId: string;
  startedAt: string;
  completedAt?: string;
  aiGraded?: boolean;
  summary: {
    totalQuestions: number;
    answeredCount: number;
    correctCount?: number;
    partialCount?: number;
    incorrectCount?: number;
    totalScore?: number;
    maxScore?: number;
    percentage?: number;
    aiFeedback?: string;
  };
}

export interface ProvAttemptFull extends ProvAttemptSummary {
  answers: Array<{
    questionId: string;
    answerText: string;
    answeredAt: string;
    aiScore?: number;
    aiFeedback?: string;
  }>;
}

export interface StatsResponse {
  setCount: number;
  attemptCount: number;
}

export interface PinVerifyResponse {
  success: boolean;
  message?: string;
  adminToken?: string;
  expiresAt?: number;
}

export interface PinStatusResponse {
  isAdmin: boolean;
  remainingMs?: number;
}

export function getProvAdminToken(): string | null {
  return localStorage.getItem(PROV_ADMIN_TOKEN_KEY);
}

export function setProvAdminToken(token: string): void {
  localStorage.setItem(PROV_ADMIN_TOKEN_KEY, token);
}

export function clearProvAdminToken(): void {
  localStorage.removeItem(PROV_ADMIN_TOKEN_KEY);
}

// =============================================================================
// Student API functions
// =============================================================================

export async function getStats(): Promise<StatsResponse> {
  const response = await provAxios.get<StatsResponse>('/api/prov/stats');
  return response.data;
}

export async function testConnection(): Promise<boolean> {
  try {
    await getStats();
    return true;
  } catch (error) {
    if (isProvTokenAuthError(error)) {
      return false;
    }
    return false;
  }
}

export interface PracticeAnswer {
  questionId: string;
  answer: string;
  acceptableAnswers?: string[];
  forms?: Array<{ label: string; value: string }>;
}

export async function getPracticeAnswers(setId: string): Promise<PracticeAnswer[]> {
  const response = await provAxios.get<{ answers: PracticeAnswer[] }>(
    `/api/prov/sets/${setId}/practice-answers`
  );
  return response.data.answers;
}

export async function listSets(): Promise<ProvSetSummary[]> {
  const response = await provAxios.get<{ sets: ProvSetSummary[] }>('/api/prov/sets');
  return response.data.sets;
}

export async function getSet(id: string): Promise<ProvSetStudent> {
  const response = await provAxios.get<ProvSetStudent>(`/api/prov/sets/${id}`);
  return response.data;
}

export interface StartAttemptResponse {
  attemptId: string;
  setId: string;
  startedAt: string;
  totalQuestions: number;
  resumed: boolean;
  existingAnswers: Record<string, string>;
}

export async function startAttempt(setId: string): Promise<StartAttemptResponse> {
  const response = await provAxios.post<StartAttemptResponse>(
    `/api/prov/sets/${setId}/attempts`
  );
  return response.data;
}

export async function saveAnswer(
  attemptId: string,
  questionId: string,
  answerText: string
): Promise<{ answeredCount: number; totalQuestions: number }> {
  const response = await provAxios.put<{ attemptId: string; answeredCount: number; totalQuestions: number }>(
    `/api/prov/attempts/${attemptId}/answers/${questionId}`,
    { answerText }
  );
  return response.data;
}

export async function completeAttempt(attemptId: string): Promise<void> {
  await provAxios.post(`/api/prov/attempts/${attemptId}/complete`);
}

export interface StudentGradeResult {
  attemptId: string;
  summary: ProvAttemptSummary['summary'];
  perQuestion: Array<{
    questionId: string;
    aiScore?: number;
    aiFeedback?: string;
    correctAnswer: string;
    acceptableAnswers?: string[];
  }>;
}

export async function studentGradeAttempt(attemptId: string): Promise<StudentGradeResult> {
  const response = await provAxios.post<StudentGradeResult>(
    `/api/prov/attempts/${attemptId}/grade`,
    undefined,
    // AI rättning kan ta längre tid för många frågor
    { timeout: 120000 }
  );
  return response.data;
}

export async function listAttempts(setId: string): Promise<ProvAttemptSummary[]> {
  const response = await provAxios.get<{ attempts: ProvAttemptSummary[] }>(
    `/api/prov/sets/${setId}/attempts`
  );
  return response.data.attempts;
}

// =============================================================================
// PIN / Admin API functions
// =============================================================================

export async function verifyPin(pin: string): Promise<PinVerifyResponse> {
  const response = await provAxios.post<PinVerifyResponse>('/api/prov/pin/verify', { pin });
  if (response.data?.adminToken) {
    setProvAdminToken(response.data.adminToken);
  }
  return response.data;
}

export async function changePin(oldPin: string, newPin: string): Promise<PinVerifyResponse> {
  const response = await provAxios.post<PinVerifyResponse>('/api/prov/pin/change', { oldPin, newPin });
  return response.data;
}

export async function logoutAdmin(): Promise<void> {
  await provAxios.post('/api/prov/pin/logout');
  clearProvAdminToken();
}

export async function getAdminStatus(): Promise<PinStatusResponse> {
  const response = await provAxios.get<PinStatusResponse>('/api/prov/pin/status');
  return response.data;
}

// =============================================================================
// Admin API functions
// =============================================================================

export async function adminListSets(): Promise<ProvSetFull[]> {
  const response = await provAxios.get<{ sets: ProvSetFull[] }>('/api/prov/admin/sets');
  return response.data.sets;
}

export async function adminGetSet(id: string): Promise<ProvSetFull> {
  const response = await provAxios.get<ProvSetFull>(`/api/prov/admin/sets/${id}`);
  return response.data;
}

export async function adminUploadImage(file: File): Promise<{ imageUrl: string }> {
  const formData = new FormData();
  formData.append('image', file);
  const response = await provAxios.post<{ imageUrl: string }>(
    '/api/prov/admin/upload-image',
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 30000 }
  );
  return response.data;
}

export async function adminCreateSet(data: {
  title: string;
  subject?: string;
  source?: string;
  type?: 'standard' | 'glosor';
  revealAfterAnswer?: boolean;
  questions: Array<{ prompt: string; answer: string; acceptableAnswers?: string[]; imageUrl?: string; forms?: ProvWordForm[] }>;
}): Promise<ProvSetFull> {
  const response = await provAxios.post<ProvSetFull>('/api/prov/admin/sets', data);
  return response.data;
}

export async function adminUpdateSet(
  id: string,
  data: Partial<{
    title: string;
    subject: string;
    source: string;
    type: 'standard' | 'glosor';
    archived: boolean;
    revealAfterAnswer: boolean;
    questions: Array<{ _id?: string; prompt: string; answer: string; acceptableAnswers?: string[]; imageUrl?: string; forms?: ProvWordForm[] }>;
  }>
): Promise<ProvSetFull> {
  const response = await provAxios.put<ProvSetFull>(`/api/prov/admin/sets/${id}`, data);
  return response.data;
}

export async function adminDeleteSet(id: string): Promise<void> {
  await provAxios.delete(`/api/prov/admin/sets/${id}`);
}

export async function adminExportSet(id: string): Promise<ProvSetExportPayload> {
  const response = await provAxios.get<ProvSetExportPayload>(`/api/prov/admin/sets/${id}/export`);
  return response.data;
}

export async function adminDeleteAttempt(attemptId: string): Promise<void> {
  await provAxios.delete(`/api/prov/admin/attempts/${attemptId}`);
}

export async function adminGetAttempts(setId: string): Promise<ProvAttemptFull[]> {
  const response = await provAxios.get<{ attempts: ProvAttemptFull[] }>(
    `/api/prov/admin/sets/${setId}/attempts`
  );
  return response.data.attempts;
}

export async function adminGradeAttempt(attemptId: string): Promise<{
  attemptId: string;
  summary: ProvAttemptSummary['summary'];
  results: Array<{ questionId: string; score: number; feedback: string }>;
}> {
  const response = await provAxios.post(
    `/api/prov/admin/attempts/${attemptId}/grade`,
    undefined,
    { timeout: 120000 }
  );
  return response.data;
}

export async function adminGetAiStatus(): Promise<{
  available: boolean;
  provider?: string;
  model?: string;
  promptVersion?: string;
}> {
  const response = await provAxios.get('/api/prov/admin/ai-status');
  return response.data;
}

export async function revealAnswer(attemptId: string, questionId: string): Promise<{ correctAnswer: string }> {
  const response = await provAxios.post<{ questionId: string; correctAnswer: string }>(
    `/api/prov/attempts/${attemptId}/reveal/${questionId}`
  );
  return response.data;
}

export async function getGlosAnswers(
  setId: string
): Promise<Array<{ questionId: string; answer: string }>> {
  const response = await provAxios.get<{ answers: Array<{ questionId: string; answer: string }> }>(
    `/api/prov/sets/${setId}/flashcard-answers`
  );
  return response.data.answers;
}

export interface ImportPreviewIssue {
  field: string;
  message: string;
  questionIndex?: number;
}

export interface ImportPreviewResponse {
  ok: boolean;
  errors: ImportPreviewIssue[];
  warnings: ImportPreviewIssue[];
  parsed?: {
    title: string;
    subject?: string;
    source?: string;
    type: 'standard' | 'glosor';
    revealAfterAnswer: boolean;
    questionCount: number;
    sampleQuestions: Array<{ prompt: string; answer: string; formCount: number }>;
  };
}

export async function adminGetImportTemplate(): Promise<{
  version: number;
  instructions: string;
  gptPrompt: string;
  set: ProvSetExportPayload['set'];
}> {
  const response = await provAxios.get('/api/prov/admin/import/template');
  return response.data;
}

export async function adminPreviewImport(data: {
  title?: string;
  subject?: string;
  source?: string;
  rawText: string;
  format?: 'auto' | 'json' | 'numbered';
}): Promise<ImportPreviewResponse> {
  const response = await provAxios.post<ImportPreviewResponse>(
    '/api/prov/admin/import/preview',
    data
  );
  return response.data;
}

export async function adminImportSet(data: {
  title?: string;
  subject?: string;
  source?: string;
  rawText: string;
  format?: 'auto' | 'json' | 'numbered';
}): Promise<{ set: ProvSetFull; importedCount: number; warnings?: ImportPreviewIssue[] }> {
  const response = await provAxios.post<{ set: ProvSetFull; importedCount: number; warnings?: ImportPreviewIssue[] }>(
    '/api/prov/admin/sets/import',
    data
  );
  return response.data;
}

// =============================================================================
// Error helper
// =============================================================================

export function getErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    // Timeout / aborted request (common when AI grading takes long)
    if (error.code === 'ECONNABORTED' || (typeof error.message === 'string' && error.message.toLowerCase().includes('timeout'))) {
      return 'Rättningen tar längre tid än vanligt. Vänta lite och försök igen.';
    }
    const data = error.response?.data as { message?: string } | undefined;
    if (data?.message) {
      return data.message;
    }
    if (error.response?.status === 401 || isProvTokenAuthError(error)) {
      return 'Ogiltig eller saknad prov-token. Koppla från enheten och aktivera med en ny token.';
    }
    if (error.response?.status === 403) {
      return 'Åtkomst nekad. Admin-PIN krävs eller sessionen har löpt ut.';
    }
    if (error.response?.status === 429) {
      return 'För många förfrågningar. Vänta en stund.';
    }
  }
  return 'Ett oväntat fel uppstod. Försök igen.';
}
